/**
 * Testimonials carousel section: owns the header (divider, title, arrows) and
 * drives the carousel via its imperative handle. The carousel stays full-bleed
 * because PeekCarousel centers its active slide against its own width, and
 * constraining it shrinks the peek.
 */
import { useRef } from 'react'
import type { TestimonialItem } from '@educator/components/TestimonialCard'
import {
  TestimonialsCarousel,
  type TestimonialsCarouselHandle,
} from '@educator/components/TestimonialsCarousel'
import { useTranslation } from '@mc/i18n'
import { Container } from '../Container'
import { PROGRAM_SECTIONS_I18N_NAMESPACE } from '../i18n'
import { CarouselHeader } from '../shared/CarouselHeader'
import { useRowScroll } from '../shared/useRowScroll'
import type { TestimonialsSectionData } from '../types/sections'
import { TESTIMONIAL_LINKS } from './constants/testimonialLinks'

interface TestimonialCopy {
  id: string
  name: string
  role?: string
  quote: string
}

/** Renaming this splits the program-page testimonial funnel. */
const TRACKING_LOCATION = 'testimonials'

interface TestimonialsSectionProps {
  data: TestimonialsSectionData
  isLinkClickable?: boolean
}

export const TestimonialsSection = ({
  data,
  isLinkClickable = true,
}: TestimonialsSectionProps) => {
  const { t } = useTranslation(PROGRAM_SECTIONS_I18N_NAMESPACE)
  const carouselRef = useRef<TestimonialsCarouselHandle>(null)
  const { scrollRow } = useRowScroll(carouselRef)

  // Merge i18n copy with per-person data (LinkedIn + avatar URLs) by id.
  const rawCopy = t(data.itemsKey, { returnObjects: true })
  const copy = Array.isArray(rawCopy) ? (rawCopy as TestimonialCopy[]) : []
  const items: TestimonialItem[] = copy.map((c) => {
    const links = TESTIMONIAL_LINKS[c.id]
    return {
      ...c,
      linkedInUrl: isLinkClickable ? links?.linkedInUrl ?? '' : '',
      avatarUrl: links?.avatarUrl ?? '',
    }
  })

  return (
    <section
      id='testimonials'
      data-section='testimonials'
      className='w-full py-6 text-edu-fg-primary md:py-12'
    >
      <Container bleed>
        <div className='flex flex-col gap-6 md:gap-4'>
          <CarouselHeader
            title={t('testimonials.headline')}
            prevLabel={t('testimonials.previousLabel')}
            nextLabel={t('testimonials.nextLabel')}
            onNav={scrollRow}
            className='px-6 md:px-25'
          />

          <TestimonialsCarousel
            ref={carouselRef}
            items={items}
            fromLinkedInLabel={t('testimonials.fromLinkedIn')}
            sectionLabel={t('testimonials.sectionLabel')}
            location={TRACKING_LOCATION}
          />
        </div>
      </Container>
    </section>
  )
}
